
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import Context from "@/mixins/context";
import RequestPromises from "@/mixins/requestPromises";
import ExternalAuthMixin from "@/components/app/global/payments/externalAuthMixin.vue";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { GatewayTypes, ConfirmModalModes } from "@/data/enums";

// Types
import { IProvider } from "@/models/providers";
import {
  IPaymentMethodsProviderData,
  IPaymentMethodsProviderMethods
} from "@/models/providers/paymentMethods";
import { IState } from "@/store";
import { IPaymentDetail } from "@/models/paymentDetails";
import { IClient } from "@/models/clients";
import { IGateway } from "@/models/gateways";

@Component({
  computed: {
    ...mapState({
      paymentMethodsScope(state: IState, getters) {
        return getters[
          `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/clientScope`
        ](this.clientId);
      },
      paymentMethods(state, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.CLIENTS_PAYMENT_DETAILS,
          scope: this.paymentMethodsScope
        });
      }
    })
  }
})
export default class ClientPaymentMethodsProvider
  extends Mixins(Context, ExternalAuthMixin, RequestPromises)
  implements IProvider {
  // Map state

  public paymentMethodsScope!: string;
  public paymentMethods!: IPaymentDetail[];

  // Props

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleGuarding: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Data

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  // Life-cycle methods

  public created() {
    if (!this.contentIsAvailable) return;

    this.checkExternalAuthQueryParams();
    this.loadData();
  }

  public mounted() {
    this.$bus.$on("refreshPaymentMethods", this.reloadData);
  }

  public beforeDestroy() {
    this.$bus.$off("refreshPaymentMethods");

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CLIENTS_PAYMENT_DETAILS,
      scope: this.paymentMethodsScope,
      vm: this
    });
  }

  // Methods

  public async loadData() {
    try {
      await this.getPaymentMethods();
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async getPaymentMethods() {
    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/list`,
      {
        id: this.clientId,
        scope: this.paymentMethodsScope,
        params: {
          limit: 0,
          order: "-default,id",
          with: "gateway,client"
        },
        vm: this,
        ignoreStored: this.isReloading
      }
    );
  }

  public reloadData() {
    this.isReloading = true;

    return this.loadData();
  }

  public verifyPaymentMethod(gateway: IGateway, fullPath: string) {
    this.redirectToExternalAuth({
      ...gateway.next_action,
      returnLocation: this.$router.resolve(fullPath).location
    });
  }

  public isVerified(paymentDetail: IPaymentDetail) {
    if (!paymentDetail.next_action) return true;

    return paymentDetail.sca_verified;
  }

  public editPaymentMethod(paymentMethod: IPaymentDetail) {
    if (
      !this.canUpdate ||
      paymentMethod.staged_import ||
      paymentMethod.client_id !== this.clientId
    )
      return;

    this.$store.dispatch(
      `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/openEditModal`,
      {
        props: {
          method: paymentMethod
        },
        events: {
          "payment-method-updated": this.reloadData
        }
      }
    );
  }

  public goToParentPaymentDetails(parentId: IClient["id"]) {
    this.$router.push({
      name: "adminClientPaymentMethods",
      params: {
        clientId: parentId
      }
    });
  }

  public async deletePaymentMethod(method: IPaymentDetail) {
    if (!this.canDelete) return;

    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          mode: ConfirmModalModes.BUTTON,
          autoClose: false,
          action: this.$t("_action.delete_object", {
            object: (this.$t("_.payment_method") as string).toLowerCase()
          }),
          message: this.$t("_sentence.delete_confirmation", {
            object: (this.$t("_.payment_method") as string).toLowerCase()
          })
        },
        canCancel: ["escape", "outside", "button"],
        events: {
          confirmed: async () => {
            try {
              await this.$store.dispatch(
                `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/remove`,
                {
                  id: method.client_id,
                  payment_details_id: method.id
                }
              );
              this.reloadData();

              this.$buefy.toast.open({
                message: this.$t("_.object_deleted", {
                  object: this.$t("_.payment_method")
                }) as string
              });
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            }
            confirm.close();
          }
        }
      }
    });
  }

  public async makeMethodDefault(paymentMethod: IPaymentDetail) {
    if (!this.canUpdate) return;
    try {
      await this.$store.dispatch(
        `data/${DataModules.CLIENTS_PAYMENT_DETAILS}/update`,
        {
          id: paymentMethod.client_id,
          payment_details_id: paymentMethod.id,
          data: {
            default: true
          }
        }
      );
      this.reloadData();
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    }
  }

  // Computed

  public get clientPaymentMethods() {
    return _.map(
      _.filter(this.paymentMethods, ["client_id", this.clientId]),
      paymentMethod => {
        const isCardGateway = paymentMethod.gateway.type === GatewayTypes.CARD;

        return {
          ...paymentMethod,
          isVerified: isCardGateway ? this.isVerified(paymentMethod) : true,
          isVerifiable: isCardGateway ? !this.isVerified(paymentMethod) : false
        };
      }
    );
  }

  public get parentPaymentMethods() {
    return _.filter(
      this.paymentMethods,
      paymentMethod => paymentMethod.client_id !== this.clientId
    );
  }

  public get parentName() {
    if (!this.parentPaymentMethods.length) return "";

    const parentPaymentMethod = this.parentPaymentMethods[0];

    return (
      parentPaymentMethod.client?.fullname ||
      parentPaymentMethod.client?.public_name ||
      ""
    );
  }

  // Guarding

  public get canUpdate() {
    return this.$userCan("update_client_payment_detail");
  }

  public get canDelete() {
    return this.$userCan("delete_client_payment_detail");
  }

  public get guardFunctionalities() {
    return ["list_client_payment_details"];
  }

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Provide

  @Provide("paymentMethodsProviderData")
  public providerData(): IPaymentMethodsProviderData {
    return {
      canDelete: this.canDelete,
      canUpdate: this.canUpdate,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      parentName: this.parentName,
      paymentMethods: this.clientPaymentMethods,
      parentPaymentMethods: this.parentPaymentMethods
    };
  }

  @Provide("paymentMethodsProviderMethods")
  public get providerMethods(): IPaymentMethodsProviderMethods {
    return {
      deletePaymentMethod: this.deletePaymentMethod,
      editPaymentMethod: this.editPaymentMethod,
      goToParentPaymentDetails: this.goToParentPaymentDetails,
      makeMethodDefault: this.makeMethodDefault,
      reloadData: this.reloadData,
      verifyPaymentMethod: this.verifyPaymentMethod
    };
  }

  // Render

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $paymentMethodsData: this.providerData(),
        $paymentMethodsMethods: this.providerMethods
      })
    );
  }
}
